/* eslint-disable react-hooks/exhaustive-deps */
import { enum_lab_result_status_enum, useLabResultsListLazyQuery } from '../../../generated/graphql'
import { useAccount } from '../../auth/hooks/useAccount'
import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react'

interface LabResultContextType {
  isLabResultAvailable: boolean
  refetch: () => Promise<void>
}

const LabResultContext = createContext<LabResultContextType | undefined>(undefined)

const useLabResultFetcher = () => {
  const [isLabResultAvailable, setIsLabResultAvailable] = useState(false)
  const [fetchLabResults] = useLabResultsListLazyQuery()

  const COMPLETED_STATUSES = [enum_lab_result_status_enum.complete, enum_lab_result_status_enum.partial]

  const checkLabResultAvailability = useCallback(async () => {
    try {
      const { data } = await fetchLabResults()

      if (!data?.lab_result?.length) {
        setIsLabResultAvailable(false)
        return
      }

      const hasCompletedResults = data.lab_result.some((labResult) => {
        const status = labResult?.lab_order?.lab_result_status
        return status && COMPLETED_STATUSES.includes(status)
      })

      setIsLabResultAvailable(hasCompletedResults)
    } catch (err) {
      setIsLabResultAvailable(false)
    }
  }, [fetchLabResults, setIsLabResultAvailable])

  return {
    isLabResultAvailable,
    refetch: checkLabResultAvailability,
  }
}

export const IsLabResultAvailableProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAccount()
  const labResultData = useLabResultFetcher()

  useEffect(() => {
    if (isAuthenticated) {
      labResultData.refetch()
    }
  }, [isAuthenticated, labResultData.refetch])

  return <LabResultContext.Provider value={labResultData}>{children}</LabResultContext.Provider>
}

export const useIsLabResultAvailable = () => {
  const context = useContext(LabResultContext)
  if (!context) {
    throw new Error('useIsLabResultAvailable must be used within an IsLabResultAvailableProvider')
  }
  return context
}
